import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Loader2,
  Search,
  ShoppingCart,
  Package,
  User,
  Calendar,
  FileText,
  Calculator,
  MapPin,
  AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { calculateSalesTotals } from '@/lib/sales-totals';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { CreateCustomerModal } from '@/components/trading/create-customer-modal';
import { CounterpartyPicker, type CounterpartySelection } from '@/components/trading/sales/counterparty-picker';
import { FinancialCorrectionDialog, type FinancialReview } from '@/components/trading/sales/financial-correction-dialog';
import type { RouterInputs } from '@polatkan/api';

type ChangeInput = RouterInputs['tradingSalesOrder']['previewUpdate'];

interface OrderItemForm {
  id?: string;
  productId?: string;
  productName?: string;
  productCode?: string;
  description?: string; // required for a service line (no productId)
  warehouseId?: string;
  quantity: number;
  shippedQty?: number;
  unitPrice: number;
  discountPercent: number;
  taxRate: number;
  notes?: string;
}

interface SalesOrderForm {
  customerId?: string;
  supplierId?: string;
  companyName?: string;
  contactName: string;
  email: string;
  phone?: string;
  shippingAddress?: string;
  shippingCity?: string;
  shippingCountry?: string;
  requiredDate?: string;
  currency: string;
  shippingCost: number;
  paymentTerms?: string;
  notes?: string;
  items: OrderItemForm[];
}

export function OrderEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromInvoice = searchParams.get('fromInvoice');
  const returnPath = fromInvoice ? `/trading/finance/invoices/${encodeURIComponent(fromInvoice)}` : `/trading/sales/orders/${id}`;
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const [productSearch, setProductSearch] = useState('');
  const [initializedOrderId, setInitializedOrderId] = useState<string>();
  const [review, setReview] = useState<FinancialReview | null>(null);
  const [pendingChange, setPendingChange] = useState<ChangeInput | null>(null);
  const [previewPending, setPreviewPending] = useState(false);

  // Fetch order data
  const { data: order, isLoading: isLoadingOrder, error: orderError } = trpc.tradingSalesOrder.byId.useQuery(
    id!,
    { enabled: !!id }
  );

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
  } = useForm<SalesOrderForm>({
    defaultValues: {
      currency: 'EUR',
      shippingCost: 0,
      items: [],
    },
  });

  const { fields: items, append: appendItem, remove: removeItem } = useFieldArray({
    control,
    name: 'items',
    keyName: 'formKey',
  });

  // Queries
  const { data: customersData } = trpc.tradingCustomer.list.useQuery({ pageSize: 100 });
  const { data: warehousesData } = trpc.warehouse.list.useQuery({});
  const { data: productsData } = trpc.tradingProduct.list.useQuery(
    { search: productSearch },
    { enabled: productSearch.length >= 2 }
  );

  const customers = customersData?.customers ?? [];
  const warehouses = warehousesData ?? [];
  const products = productsData?.products ?? [];

  const watchedValues = watch();
  const selectedCustomerId = watchedValues.customerId;
  const hasCounterparty = !!(watchedValues.customerId || watchedValues.supplierId);
  const counterpartyLocked = !!(order?.deliveries.length || order?.invoices.length);

  // Get default warehouse
  const defaultWarehouse = warehouses.find((w) => w.isDefault) || warehouses[0];

  // Initialize form with order data
  useEffect(() => {
    if (order && initializedOrderId !== order.id) {
      reset({
        customerId: order.tradingCustomerId || undefined,
        supplierId: order.tradingSupplierId || undefined,
        companyName: order.companyName || undefined,
        contactName: order.contactName,
        email: order.email,
        phone: order.phone || undefined,
        shippingAddress: order.shippingAddress || undefined,
        shippingCity: order.shippingCity || undefined,
        shippingCountry: order.shippingCountry || undefined,
        requiredDate: order.requiredDate ? format(new Date(order.requiredDate), 'yyyy-MM-dd') : undefined,
        currency: order.currency,
        shippingCost: order.shippingCost,
        paymentTerms: order.paymentTerms || undefined,
        notes: order.notes || undefined,
        items: order.items.map((item) => ({
          id: item.id,
          productId: item.productId ?? undefined,
          productName: item.productName,
          productCode: item.productCode ?? undefined,
          description: item.description,
          warehouseId: item.warehouseId ?? undefined,
          quantity: item.quantity,
          shippedQty: item.shippedQty,
          unitPrice: item.unitPrice,
          discountPercent: item.discountPercent,
          taxRate: item.taxRate,
          notes: item.notes || undefined,
        })),
      });
      setInitializedOrderId(order.id);
    }
  }, [order, initializedOrderId, reset]);

  const utils = trpc.useUtils();

  const updateMutation = trpc.tradingSalesOrder.update.useMutation({
    onSuccess: async () => {
      await utils.invalidate();
      showSuccessToast(t('common.saved'));
      navigate(returnPath);
    },
    onError: (error) => showErrorToast(error.message),
  });

  const globalDiscountPercent = order?.subtotal
    ? order.discountAmount / order.subtotal * 100 : 0;
  const { subtotal, discountAmount, taxAmount, total: itemsTotal } = calculateSalesTotals(
    watchedValues.items, globalDiscountPercent
  );
  const total = itemsTotal + (watchedValues.shippingCost || 0);

  const handleAddProduct = async (productId: string) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;

    // Check if product already exists in items
    const existingIndex = items.findIndex((item) => item.productId === productId);
    if (existingIndex >= 0) {
      // Increment quantity instead of adding new line
      const currentQty = watchedValues.items[existingIndex]?.quantity || 0;
      setValue(`items.${existingIndex}.quantity`, currentQty + 1);
      return;
    }

    let unitPrice = 0;
    let taxRate = 0;
    try {
      const resolved = await utils.tradingProduct.resolvePrice.fetch({
        productId: product.id,
        kind: watchedValues.supplierId ? 'vendor' : 'customer',
      });
      unitPrice = resolved.unitPrice ?? 0;
      taxRate = resolved.kdvRate ?? 0;
      if (resolved.unitPrice == null) showErrorToast(t('products.priceNotConfigured'));
    } catch {
      showErrorToast(t('products.priceLoadError'));
    }

    appendItem({
      productId: product.id,
      productName: product.name,
      productCode: product.fullCode,
      warehouseId: defaultWarehouse?.id || '',
      quantity: 1,
      unitPrice,
      discountPercent: 0,
      taxRate,
      notes: '',
    });

    setProductSearch('');
  };

  const handleAddCustomLine = () => {
    appendItem({
      description: '',
      quantity: 1,
      unitPrice: 0,
      discountPercent: 0,
      taxRate: 0,
      notes: '',
    });
  };

  const handleCustomerSelect = (customerId: string) => {
    setValue('customerId', customerId);
    setValue('supplierId', undefined);
    const customer = customers.find((c) => c.id === customerId);
    if (customer) {
      setValue('contactName', customer.contactName || '');
      setValue('email', customer.email || '');
      setValue('phone', customer.phone || '');
      setValue('companyName', customer.companyName || '');
      setValue('shippingAddress', customer.addressLine1 || '');
      setValue('shippingCity', customer.city || '');
      setValue('shippingCountry', customer.country || '');
    }
  };

  const handleCounterpartySelect = (selection: CounterpartySelection) => {
    setValue('customerId', selection.kind === 'customer' ? selection.id : undefined);
    setValue('supplierId', selection.kind === 'vendor' ? selection.id : undefined);
    setValue('companyName', selection.companyName || '');
    setValue('contactName', selection.contactName || '');
    setValue('email', selection.email || '');
    setValue('phone', selection.phone || '');
    if (selection.kind === 'customer') handleCustomerSelect(selection.id);
  };

  const handleCustomerCreated = (customer: { id: string; companyName: string; contactName: string; email: string; phone: string | null }) => {
    setValue('customerId', customer.id);
    setValue('supplierId', undefined);
    setValue('companyName', customer.companyName || '');
    setValue('contactName', customer.contactName || '');
    setValue('email', customer.email || '');
    setValue('phone', customer.phone || '');
  };

  const onSubmit = async (data: SalesOrderForm) => {
    if (!id || previewPending || updateMutation.isPending) return;

    if (!data.customerId && !data.supplierId && (!data.contactName || !data.email)) {
      showErrorToast(t('sales.validation.enterContactAndEmail'));
      return;
    }

    if (data.items.length === 0) {
      showErrorToast(t('sales.validation.addProduct'));
      return;
    }

    // Validate all PRODUCT items have a warehouse; service lines don't need one
    const missingWarehouse = data.items.some((item) => item.productId && !item.warehouseId);
    if (missingWarehouse) {
      showErrorToast(t('sales.validation.selectWarehouse'));
      return;
    }

    // Validate every line has either a product or a typed description
    const missingDescription = data.items.some((item) => !item.productId && !item.description?.trim());
    if (missingDescription) {
      showErrorToast(t('sales.validation.enterDescription'));
      return;
    }

    try {
      setPreviewPending(true);
      const change: ChangeInput = {
        id,
        data: {
          tradingCustomerId: data.customerId || null,
          tradingSupplierId: data.supplierId || null,
          companyName: data.companyName || null,
          contactName: data.contactName,
          email: data.email,
          phone: data.phone || null,
          shippingAddress: data.shippingAddress || null,
          shippingCity: data.shippingCity || null,
          shippingCountry: data.shippingCountry || null,
          requiredDate: data.requiredDate ? order?.requiredDate && data.requiredDate === format(new Date(order.requiredDate), 'yyyy-MM-dd') ? new Date(order.requiredDate) : new Date(data.requiredDate + 'T12:00:00Z') : null,
          shippingCost: data.shippingCost,
          paymentTerms: data.paymentTerms || null,
          notes: data.notes || null,
        },
        items: data.items.map((item) => ({
          id: item.id,
          productId: item.productId || undefined,
          warehouseId: item.warehouseId || undefined,
          description: item.productId ? undefined : item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discountPercent: item.discountPercent,
          taxRate: item.taxRate,
          notes: item.notes || null,
        })),
      };
      const preview = await utils.tradingSalesOrder.previewUpdate.fetch(change, { staleTime: 0 });
      setPendingChange(change);
      setReview(preview);
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : tCommon('error'));
    } finally {
      setPreviewPending(false);
    }
  };

  const confirmCorrection = async (correctionReason: string) => {
    if (!pendingChange || !review || updateMutation.isPending) return;
    try {
      await updateMutation.mutateAsync({ ...pendingChange, reviewToken: review.token, correctionReason });
    } catch {
      setReview(null);
      setPendingChange(null);
    }
  };

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId);

  // Loading state
  if (isLoadingOrder) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{tCommon('loading')}</span>
      </div>
    );
  }

  // Error state
  if (orderError || !order) {
    return (
      <div className="space-y-4">
        <Link
          to="/trading/sales/orders"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </Link>
        <div className="border border-red-300 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{tCommon('error')}</p>
          <p className="text-sm">{orderError?.message || t('sales.orders.notFound')}</p>
        </div>
      </div>
    );
  }

  const isPending = updateMutation.isPending || previewPending;

  return (
    <div className="space-y-6">
      <FinancialCorrectionDialog review={review} pending={updateMutation.isPending} onClose={() => { setReview(null); setPendingChange(null); }} onConfirm={reason => void confirmCorrection(reason)} />
      {fromInvoice && <div className="rounded border bg-muted/40 p-3 text-sm">{t('sales.correction.fromInvoice')}</div>}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to={returnPath}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{t('sales.orders.editOrder')}</h1>
            <p className="text-muted-foreground font-mono">{order.orderNumber}</p>
          </div>
        </div>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={isPending}
        >
          {isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          {t('sales.correction.review')}
        </Button>
      </div>

      {(order.status !== 'DRAFT' || order.invoices.length > 0) && (
        <div className="flex gap-3 border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <p>{t('sales.orders.correctionNotice')}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Customer Section */}
          <div className="border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <User className="h-5 w-5" />
              {t('sales.orders.customer')}
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('sales.orders.selectCustomer')}</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <CounterpartyPicker
                      customerId={watchedValues.customerId}
                      supplierId={watchedValues.supplierId}
                      onSelect={handleCounterpartySelect}
                      onClear={() => {
                        setValue('customerId', undefined);
                        setValue('supplierId', undefined);
                      }}
                      disabled={counterpartyLocked}
                      selectedLabel={watchedValues.companyName || watchedValues.contactName}
                    />
                  </div>
                  {!counterpartyLocked && <CreateCustomerModal onCustomerCreated={handleCustomerCreated} />}
                </div>
                {counterpartyLocked && <p className="text-xs text-muted-foreground">{t('sales.orders.counterpartyLocked')}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="companyName">{t('sales.orders.company')}</Label>
                <Input
                  id="companyName"
                  {...register('companyName')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactName">{t('sales.orders.contactName')}{!hasCounterparty && ' *'}</Label>
                <Input
                  id="contactName"
                  {...register('contactName', { required: !hasCounterparty })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('sales.orders.email')}{!hasCounterparty && ' *'}</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email', { required: !hasCounterparty })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">{t('sales.orders.phone')}</Label>
                <Input
                  id="phone"
                  {...register('phone')}
                />
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <MapPin className="h-5 w-5" />
              {t('sales.orders.shippingAddress')}
            </h2>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2 sm:col-span-3">
                <Label htmlFor="shippingAddress">{t('sales.orders.address')}</Label>
                <Input
                  id="shippingAddress"
                  {...register('shippingAddress')}
                  placeholder={t('sales.orders.addressPlaceholder')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="shippingCity">{t('sales.orders.city')}</Label>
                <Input
                  id="shippingCity"
                  {...register('shippingCity')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="shippingCountry">{t('sales.orders.country')}</Label>
                <Input
                  id="shippingCountry"
                  {...register('shippingCountry')}
                />
              </div>
            </div>
          </div>

          {/* Order Details */}
          <div className="border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <Calendar className="h-5 w-5" />
              {t('sales.orders.orderDetails')}
            </h2>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="requiredDate">{t('sales.orders.requiredDate')}</Label>
                <Input
                  type="date"
                  id="requiredDate"
                  {...register('requiredDate')}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('sales.orders.currency')}</Label>
                <Combobox
                  options={[
                    { value: 'EUR', label: 'EUR' },
                    { value: 'USD', label: 'USD' },
                    { value: 'GBP', label: 'GBP' },
                    { value: 'TRY', label: 'TRY' },
                  ]}
                  value={watchedValues.currency}
                  onValueChange={(value) => setValue('currency', value)}
                  placeholder={t('sales.orders.currency')}
                  searchPlaceholder="Search..."
                  disabled
                />
                <p className="text-xs text-muted-foreground">{t('sales.orders.currencyNotEditable')}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="shippingCost">{t('sales.orders.shipping')}</Label>
                <Input id="shippingCost" type="number" min="0" step="0.01"
                  {...register('shippingCost', { valueAsNumber: true, min: 0 })} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="paymentTerms">{t('sales.orders.paymentTerms')}</Label>
                <Input
                  id="paymentTerms"
                  {...register('paymentTerms')}
                  placeholder={t('sales.orders.paymentTermsPlaceholder')}
                />
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="flex items-center gap-2 font-semibold">
                <Package className="h-5 w-5" />
                {t('sales.orders.items')} ({items.length})
              </h2>
              <Button type="button" variant="outline" size="sm" onClick={handleAddCustomLine}>
                <Plus className="mr-1 h-4 w-4" />
                {t('sales.orders.addCustomLine')}
              </Button>
            </div>

            {/* Product Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('products.searchPlaceholder')}
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="pl-10"
                />
              </div>

              {productSearch.length === 0 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {t('sales.orders.searchHint')}
                </p>
              )}

              {productSearch.length > 0 && productSearch.length < 2 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {t('sales.orders.typeMoreChars')}
                </p>
              )}

              {productSearch.length >= 2 && products.length > 0 && (
                <div className="mt-2 border border-border divide-y max-h-48 overflow-y-auto">
                  {products.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      className="w-full p-3 text-left hover:bg-muted/50 transition-colors flex items-center justify-between"
                      onClick={() => handleAddProduct(product.id)}
                    >
                      <div>
                        <p className="font-medium text-sm">{product.name}</p>
                        <p className="text-xs text-muted-foreground">{product.fullCode}</p>
                      </div>
                      <Plus className="h-4 w-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}

              {productSearch.length >= 2 && products.length === 0 && (
                <p className="mt-2 text-sm text-muted-foreground">
                  {t('products.noProducts')}
                </p>
              )}
            </div>

            {/* Items List */}
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center border border-border bg-muted/30">
                <ShoppingCart className="h-10 w-10 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">
                  {t('sales.orders.noItems')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('sales.orders.searchAndAddProducts')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((field, index) => {
                  const item = watchedValues.items[index];
                  const discountedPrice = (item?.unitPrice || 0) * (1 - (item?.discountPercent || 0) / 100);
                  const lineSubtotal = (item?.quantity || 0) * discountedPrice;
                  const lineTax = lineSubtotal * ((item?.taxRate || 0) / 100);
                  const lineTotal = lineSubtotal + lineTax;

                  return (
                    <div key={field.formKey} className="border border-border p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          {field.productId ? (
                            <>
                              <p className="font-medium">{field.productName}</p>
                              <p className="text-xs text-muted-foreground font-mono">{field.productCode}</p>
                            </>
                          ) : (
                            <div className="space-y-1">
                              <Label className="text-xs">{t('sales.orders.customLineDescription')}</Label>
                              <Input
                                {...register(`items.${index}.description`)}
                                placeholder={t('sales.orders.customLineDescriptionPlaceholder')}
                              />
                            </div>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          disabled={(item?.shippedQty || 0) > 0}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>

                      {(item?.shippedQty || 0) > 0 && (
                        <p className="mb-3 text-xs text-muted-foreground">
                          {t('sales.orders.shippedQuantity', { quantity: item?.shippedQty })}
                        </p>
                      )}
                      <div className="grid gap-3 sm:grid-cols-6">
                        {field.productId && (
                          <div className="space-y-1 sm:col-span-2">
                            <Label className="text-xs">{t('sales.orders.warehouse')}</Label>
                            <Combobox
                              options={warehouses.map((w) => ({
                                value: w.id,
                                label: `${w.code} - ${w.name}`,
                              }))}
                              value={item?.warehouseId || ''}
                              onValueChange={(value) => setValue(`items.${index}.warehouseId`, value)}
                              placeholder={t('sales.orders.selectWarehouse')}
                              searchPlaceholder="Search..."
                              disabled={(item?.shippedQty || 0) > 0}
                            />
                          </div>
                        )}
                        <div className="space-y-1">
                          <Label className="text-xs">{t('sales.orders.quantity')}</Label>
                          <Input
                            type="number"
                            {...register(`items.${index}.quantity`, { valueAsNumber: true })}
                            min={item?.shippedQty || 0.01}
                            step="0.01"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t('sales.orders.unitPrice')}</Label>
                          <Input
                            type="number"
                            {...register(`items.${index}.unitPrice`, { valueAsNumber: true })}
                            step="0.01"
                            min="0"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t('sales.orders.discount')} %</Label>
                          <Input
                            type="number"
                            {...register(`items.${index}.discountPercent`, { valueAsNumber: true })}
                            step="0.01"
                            min="0"
                            max="100"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t('sales.orders.tax')} %</Label>
                          <Input
                            type="number"
                            {...register(`items.${index}.taxRate`, { valueAsNumber: true })}
                            step="0.01"
                            min="0"
                            max="100"
                          />
                        </div>
                      </div>

                      <div className="mt-3 space-y-1">
                        <Label className="text-xs">{t('sales.orders.notes')}</Label>
                        <Input {...register(`items.${index}.notes`)} />
                      </div>

                      <div className="flex justify-end mt-3 pt-3 border-t">
                        <span className="text-sm">
                          <span className="text-muted-foreground">{t('sales.orders.lineTotal_label')}</span>
                          <span className="font-medium">
                            {formatCurrency(lineTotal, watchedValues.currency)}
                          </span>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <FileText className="h-5 w-5" />
              {t('sales.orders.notes')}
            </h2>

            <div className="space-y-2">
              <Label htmlFor="notes">{t('sales.orders.internalNotes')}</Label>
              <Textarea
                id="notes"
                {...register('notes')}
                placeholder={t('sales.orders.notesPlaceholder')}
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Sidebar - Order Summary */}
        <div className="space-y-6">
          <div className="border border-border bg-card p-6 sticky top-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <Calculator className="h-5 w-5" />
              {t('sales.orders.summary')}
            </h2>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('sales.orders.orderNumber')}</span>
                <span className="font-mono font-medium">{order.orderNumber}</span>
              </div>

              {selectedCustomer && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('sales.orders.customer')}</span>
                  <span className="font-medium">{selectedCustomer.companyName || selectedCustomer.contactName}</span>
                </div>
              )}

              {watchedValues.requiredDate && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('sales.orders.requiredDate')}</span>
                  <span className="font-medium">
                    {format(new Date(watchedValues.requiredDate), 'MMM d, yyyy')}
                  </span>
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('sales.orders.items')}</span>
                <span className="font-medium">{items.length}</span>
              </div>

              <div className="border-t my-3" />

              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('sales.orders.subtotal')}</span>
                <span>{formatCurrency(subtotal, watchedValues.currency)}</span>
              </div>

              {discountAmount > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('sales.orders.discount')}</span>
                  <span>-{formatCurrency(discountAmount, watchedValues.currency)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('sales.orders.shipping')}</span>
                <span>{formatCurrency(watchedValues.shippingCost || 0, watchedValues.currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('sales.orders.tax')}</span>
                <span>{formatCurrency(taxAmount, watchedValues.currency)}</span>
              </div>

              <div className="flex justify-between text-base pt-2 border-t">
                <span className="font-semibold">{t('sales.orders.total')}</span>
                <span className="font-bold">
                  {formatCurrency(total, watchedValues.currency)}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 space-y-2">
              <Button
                type="button"
                className="w-full"
                onClick={handleSubmit(onSubmit)}
                disabled={isPending || items.length === 0}
              >
                {isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t('sales.correction.review')}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate(returnPath)}
              >
                {t('common.cancel')}
              </Button>
            </div>

            {/* Validation Warnings */}
            {((!hasCounterparty && (!watchedValues.contactName || !watchedValues.email)) || items.length === 0) && (
              <div className="mt-4 space-y-2">
                {!hasCounterparty && !watchedValues.contactName && (
                  <p className="text-xs text-yellow-600">{t('sales.validation.enterContactName')}</p>
                )}
                {!hasCounterparty && !watchedValues.email && (
                  <p className="text-xs text-yellow-600">{t('sales.validation.enterEmail')}</p>
                )}
                {items.length === 0 && (
                  <p className="text-xs text-yellow-600">{t('sales.validation.addAtLeastOneProduct')}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
